/** Komoot: connect once, pick a planned Komoot route for a day, open our routes in Komoot's planner. */
import { useState, type FormEvent } from "react";
import { safeUrl } from "@/api/client";
import {
  useConnections,
  useKomootImport,
  useKomootLogin,
  useKomootPreview,
  useKomootTours,
} from "@/api/hooks";
import type { KomootPreview } from "@/api/schemas";
import { dayLong, km, meters, num } from "@/lib/format";
import { ElevationProfile } from "./ElevationProfile";
import { RouteMap } from "./RouteMap";
import { useToast } from "./Toast";
import { Dialog, Empty, ErrorBox, Skeleton } from "./ui";

/** Komoot is owner-only: every Komoot button shows only when a Komoot connection exists (connected in
 * Settings → Data sources), never as a prompt for a Komoot password. */
/** "route +40 min / +600 m vs plan"; says so when the day has no planned ride. */
export function diffLine(p: Pick<KomootPreview, "diff">): string {
  if (!p.diff) return "No ride planned that day";
  const sg = (n: number, unit: string) => `${n >= 0 ? "+" : "−"}${Math.abs(Math.round(n))} ${unit}`;
  const parts = [sg(p.diff.minutes, "min")];
  if (p.diff.ascent_m != null) parts.push(sg(p.diff.ascent_m, "m"));
  return `route ${parts.join(" / ")} vs plan`;
}

export function useKomootConnected(): boolean {
  const q = useConnections();
  return (q.data ?? []).some((c) => c.provider === "komoot" && c.status !== "not_connected");
}

export function KomootLogin({ onDone }: { onDone: () => void }) {
  const login = useKomootLogin();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      const r = await login.mutateAsync({ email, password });
      setPassword("");
      toast.ok(`Komoot connected as ${r.display_name}`);
      onDone();
    } catch (x) {
      toast.error(x);
    }
  }
  return (
    <form className="stack" onSubmit={submit}>
      <label>
        Komoot email
        <input
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      <label>
        Komoot password
        <input
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </label>
      <small className="faint">
        Used once to get Komoot&apos;s access token; the password is never stored. The app only reads your
        planned routes.
      </small>
      <button className="primary" disabled={login.isPending}>
        {login.isPending ? "Connecting…" : "Connect Komoot"}
      </button>
    </form>
  );
}

/** "Use a Komoot route": the athlete's planned Komoot routes; importing one makes it the day's route. */
export function KomootPicker({ day, onDone }: { day: string; onDone?: () => void }) {
  const connected = useKomootConnected();
  const [open, setOpen] = useState(false);
  const tours = useKomootTours(open && connected);
  const imp = useKomootImport();
  const [looking, setLooking] = useState<{ id: string; name: string } | null>(null);
  const preview = useKomootPreview(looking?.id ?? null, day);
  const toast = useToast();
  const close = () => {
    setOpen(false);
    setLooking(null);
  };
  if (!connected) return null;
  return (
    <>
      <button className="small" onClick={() => setOpen(true)}>
        Use a Komoot route
      </button>
      <Dialog open={open} onClose={close} title={`Komoot route for ${dayLong(day)}`}>
        {looking ? (
          <div className="stack" data-testid="komoot-preview">
            <b>{looking.name}</b>
            {preview.isLoading ? (
              <Skeleton height={300} />
            ) : preview.error && !preview.data ? (
              <ErrorBox error={preview.error} retry={() => preview.refetch()} />
            ) : preview.data ? (
              <>
                <RouteMap
                  geometry={preview.data.geometry}
                  segments={preview.data.analysis?.segments ?? null}
                  label={`Map of ${preview.data.name}`}
                />
                {preview.data.analysis && <ElevationProfile analysis={preview.data.analysis} height={140} />}
                <div className="kpi-row">
                  <span>
                    <b>{km(preview.data.distance_km)}</b>
                  </span>
                  <span>
                    <b>{meters(preview.data.ascent_m)}</b>
                  </span>
                  <span>
                    ~<b>{num(preview.data.est_hours, 1)} h</b>
                  </span>
                </div>
                <p data-testid="komoot-diff">{diffLine(preview.data)}</p>
              </>
            ) : null}
            <div className="row">
              <button
                className="primary small"
                disabled={imp.isPending || !preview.data}
                onClick={() =>
                  imp.mutateAsync({ id: looking.id, day }).then(
                    (r) => {
                      toast.ok(`${looking.name} is the route for ${dayLong(day)} (${num(r.est_hours, 1)} h)`);
                      close();
                      onDone?.();
                    },
                    (e: unknown) => toast.error(e),
                  )
                }
              >
                Confirm
              </button>
              <button className="small" disabled={imp.isPending} onClick={() => setLooking(null)}>
                Back
              </button>
            </div>
          </div>
        ) : tours.isLoading ? (
          <Skeleton height={200} />
        ) : tours.error && !tours.data ? (
          <ErrorBox error={tours.error} retry={() => tours.refetch()} />
        ) : (tours.data ?? []).length === 0 ? (
          <Empty>No planned routes in your Komoot account yet. Plan one in Komoot, then come back.</Empty>
        ) : (
          <div className="list" data-testid="komoot-tours">
            {(tours.data ?? []).map((t) => (
              <div key={t.id} className="row between komoot-tour">
                <div>
                  <b>{t.name}</b>
                  <br />
                  <small className="muted">
                    {km(t.distance_km)} · {meters(t.ascent_m)} · Komoot {num(t.duration_h, 1)} h
                    {t.imported_for && <> · used for {dayLong(t.imported_for)}</>}
                  </small>
                </div>
                <button className="primary small" onClick={() => setLooking({ id: t.id, name: t.name })}>
                  Use
                </button>
              </div>
            ))}
          </div>
        )}
      </Dialog>
    </>
  );
}

/** Opens the route in Komoot: its own page for imported routes, else Komoot's planner with the
 * route's waypoints, Gravel riding and Komoot's routing on known ways. Save it there; it syncs to the Edge. */
export function OpenInKomoot({ url }: { url: string | null }) {
  const href = url ? safeUrl(url) : null;
  if (!href) return null;
  const planned = href.includes("/plan/");
  return (
    <a
      className="btn small"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={
        planned
          ? "Opens Komoot's planner (Gravel, known ways). Save it there; it syncs to your Edge."
          : undefined
      }
    >
      {planned ? "Open in Komoot" : "View in Komoot"}
    </a>
  );
}
